import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import { colors } from '../../theme';

/**
 * Saved-address chooser shared by the booking and checkout flows.
 *
 * Both screens need the same list, the same "add new" shortcut and the same
 * default-address preselection, so it lives here once. `onLoaded` hands the
 * fetched list back so the parent can preselect.
 */
const AddressPicker = ({ title = 'Delivery address', value, onChange, onLoaded }) => {
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.addresses.list()
      .then((res) => {
        const list = res.data || [];
        setAddresses(list);
        if (onLoaded) onLoaded(list);
      })
      .catch(() => setAddresses([]))
      .finally(() => setLoading(false));
  }, [onLoaded]);

  useEffect(() => { load(); }, [load]);

  // A new address added in another tab (or the address page) should show up
  // when the customer comes back without a full reload.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  return (
    <div className="card sh-sm">
      <div className="row-between" style={{ marginBottom: 12 }}>
        <h3 className="t-title">{title}</h3>
        <button
          type="button"
          className="link-btn"
          onClick={() => navigate('/addresses/new', { state: { returnTo: window.location.pathname } })}
        >
          + Add new
        </button>
      </div>

      {loading ? (
        <div className="t-small">Loading addresses…</div>
      ) : !addresses.length ? (
        <button
          type="button"
          onClick={() => navigate('/addresses/new', { state: { returnTo: window.location.pathname } })}
          style={{
            display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
            background: '#fff', border: `1px dashed ${colors.line}`, borderRadius: 16, padding: 16,
          }}
        >
          <span style={{ fontSize: 20 }}>📍</span>
          <span className="t-body grow">Add the address where you need this</span>
          <span style={{ fontSize: 18, color: colors.primary }}>›</span>
        </button>
      ) : addresses.map((a) => {
        const active = value === a._id;
        return (
          <button
            key={a._id}
            type="button"
            onClick={() => onChange(a._id)}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%', textAlign: 'left',
              background: active ? colors.primaryLight : '#fff',
              border: `1.4px solid ${active ? colors.primary : colors.line}`,
              borderRadius: 16, padding: 12, marginBottom: 8,
              transition: 'border-color .16s ease, background .16s ease',
            }}
          >
            <span
              style={{
                width: 20, height: 20, borderRadius: 10, marginTop: 2, flex: '0 0 auto',
                border: `${active ? 6 : 2}px solid ${active ? colors.primary : colors.line}`,
              }}
            />
            <span className="grow">
              <span className="t-title" style={{ display: 'block' }}>
                {(a.label || '').toUpperCase()}
              </span>
              <span className="t-small clamp-2" style={{ display: 'block', marginTop: 2 }}>
                {[a.line1, a.landmark, a.city, a.pincode].filter(Boolean).join(', ')}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default AddressPicker;
